import React from 'react';
import { FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import EnvVarEditor from './EnvVarEditor';

/**
 * Step 5: Environment Variables
 * - Dynamic key-value pairs
 * - Security warning
 * - Variable descriptions and required flags
 */
const MCPServerFormStep5 = ({ formData, updateFormData }) => {
  const handleEnvVarsChange = (environmentVars) => {
    updateFormData({ environmentVars });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Environment Variables
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Define the environment variables needed to configure your server.
        </p>
      </div>

      {/* Security Warning */}
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FaExclamationTriangle className="text-red-600 dark:text-red-400 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900 dark:text-red-300 mb-1">
              Security Warning
            </h3>
            <p className="text-sm text-red-800 dark:text-red-400">
              <strong>Never include actual API keys, passwords, or secrets in this configuration!</strong>
              <br />
              Only specify which environment variables are needed. Users will provide their own
              values when setting up your server.
            </p>
          </div>
        </div>
      </div>

      {/* Environment Variables Editor */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Environment Variables
        </label>
        <EnvVarEditor
          environmentVars={formData.environmentVars}
          onChange={handleEnvVarsChange}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          Specify variable names and descriptions. Users will set the actual values.
        </p>
      </div>

      {/* Examples */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
          Environment Variable Examples
        </h3>
        <div className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
          <div>
            <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs">
              API_KEY
            </code>
            <span className="ml-2">- API key for authentication</span>
          </div>
          <div>
            <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs">
              DATABASE_URL
            </code>
            <span className="ml-2">- PostgreSQL connection string</span>
          </div>
          <div>
            <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs">
              LOG_LEVEL
            </code>
            <span className="ml-2">- Logging verbosity (debug, info, warn, error)</span>
          </div>
          <div>
            <code className="bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs">
              PORT
            </code>
            <span className="ml-2">- Server port number (default: 3000)</span>
          </div>
        </div>
      </div>

      {/* No Variables Info */}
      {Object.keys(formData.environmentVars).length === 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No environment variables defined. If your server doesn't require any configuration,
            you can skip this step.
          </p>
        </div>
      )}
    </div>
  );
};

export default MCPServerFormStep5;
